import { injectable, inject } from 'inversify'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'

/**
 * Turns a zod issue into a sentence in the user's language.
 *
 * Zod ships English default messages, and rendering them would put copy
 * outside TranslationsConfig — §U's rule, broken by a library rather than by a
 * developer. So issues are mapped by *code* to a translation key, and the raw
 * `issue.message` is never shown.
 *
 * `fieldLabelKey` lets a schema say what a field is called ("Email", not
 * "email"), because the field name is copy too.
 */
@injectable()
export class ValidationMessages {
  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  /**
   * Zod issue codes we handle explicitly. Anything else falls back to a
   * generic "this value is not valid", which is a worse message but never a
   * missing or English-only one.
   */
  private readonly keyByCode: Record<string, string> = {
    invalid_type: 'validation.required',
    too_small: 'validation.tooShort',
    too_big: 'validation.tooLong',
    invalid_format: 'validation.invalidFormat',
    invalid_value: 'validation.invalidValue',
  }

  messageFor(code: string, fieldLabelKey: string | null, params: Record<string, string | number> = {}): string {
    const key = this.keyFor(code, params)
    const field = fieldLabelKey === null ? '' : this.i18nRepository.t(fieldLabelKey)

    return this.i18nRepository.t(key, { field, ...params })
  }

  /**
   * `.min(1)` on a string is not a length rule, it is the *required* rule —
   * that is what "at least one character" means. Without this an empty field
   * reads "Email must be at least 1 characters", which is technically true and
   * useless. Not a special case bolted on: it is the definition of the rule.
   */
  private keyFor(code: string, params: Record<string, string | number>): string {
    if (code === 'too_small' && String(params.min) === '1') return 'validation.required'
    return this.keyByCode[code] ?? 'validation.invalid'
  }
}
